// JSON-LD structured data for /travel-credit-card (see the CBCC SEO guide).
// One schema.org `@graph` with three nodes — WebPage, FinancialProduct/Product,
// and FAQPage — injected as a <script type="application/ld+json"> in the route
// head so crawlers get rich-result eligibility (product + FAQ) on the SSR'd HTML.
//
// FAQ entries are sourced from the SAME hardcoded MANAGE_CARD_FAQ.items the page
// renders (single source of truth — no separate fallback file, no per-user CMS
// dependency that a logged-out crawler wouldn't see). The "Q. " display prefix
// is stripped for the schema `name`.
import { SEO_CONTENT } from './content';
import { MANAGE_CARD_FAQ } from './manage-card-content';

const { title, description, canonicalUrl, cardImage } = SEO_CONTENT;

// strip the leading "Q. " / "Q." display prefix used in the on-page accordion
const stripQ = (s: string) => s.replace(/^\s*Q\.?\s*/i, '').trim();

function buildGraph() {
	const faqEntities = MANAGE_CARD_FAQ.items.map((item) => ({
		'@type': 'Question',
		name: stripQ(item.title),
		acceptedAnswer: { '@type': 'Answer', text: item.body },
	}));

	return {
		'@context': 'https://schema.org',
		'@graph': [
			{
				'@type': 'WebPage',
				url: canonicalUrl,
				headline: 'ixigo AU Travel Credit Card',
				description,
				name: title,
			},
			{
				'@type': ['FinancialProduct', 'Product'],
				name: 'ixigo AU Credit Card',
				alternateName: 'ixigo AU Travel Credit Card',
				description,
				url: canonicalUrl,
				image: cardImage,
				brand: { '@type': 'Brand', name: 'ixigo' },
				provider: {
					'@type': 'BankOrCreditUnion',
					name: 'AU Small Finance Bank',
					url: 'https://www.aubank.in/',
				},
				feesAndCommissionsSpecification:
					'Lifetime free credit card. Zero joining fee and zero annual renewal fee.',
				annualPercentageRate: {
					'@type': 'QuantitativeValue',
					value: 0,
					unitText: '% annual fee',
				},
				offers: {
					'@type': 'Offer',
					price: '0',
					priceCurrency: 'INR',
					description:
						'Lifetime free – joining fee waived off and renewal fee waived off',
				},
				additionalProperty: [
					{
						name: 'Card Network Options',
						value: 'RuPay or Visa (choose at the time of application)',
					},
					{
						name: 'Reward Points – Online Spends',
						value: 'Reward points for every ₹200 spent online',
					},
					{
						name: 'Reward Points – Offline Spends',
						value: 'Reward points for every ₹200 spent offline',
					},
					{
						name: 'Reward Points – UPI via Credit Card',
						value: 'Reward points for every ₹200 spent via Credit Card on UPI',
					},
					{ name: 'Forex Markup Fee', value: 'Zero forex markup fee' },
					{
						name: 'Train Booking Benefit',
						value: 'Zero payment gateway charges on train bookings',
					},
					{
						name: 'Fuel Surcharge Waiver',
						value: 'Fuel surcharge waiver applicable',
					},
					{
						name: 'UPI Payments',
						value:
							'Link RuPay card with any UPI app to make scan-and-pay transactions',
					},
					{
						name: 'Insurance Cover (RuPay)',
						value: 'Insurance cover of up to ₹2 lakh on RuPay Cards',
					},
					{
						name: 'Concierge Service (RuPay)',
						value: '24/7 concierge services available with RuPay Cards',
					},
				],
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: '4.8',
					reviewCount: '196502',
				},
				termsOfService:
					'https://www.aubank.in/personal-banking/credit-cards/ixigo-au-credit-card',
			},
			{ '@type': 'FAQPage', mainEntity: faqEntities },
		],
	};
}

// Serialized + XSS-escaped JSON-LD ready for a <script> tag's children. The
// `<` → < replacement stops a literal "</script>" inside any answer text
// from breaking out of the JSON-LD block.
export function cbccStructuredDataJson(): string {
	return JSON.stringify(buildGraph()).replace(/</g, '\\u003c');
}
